// utils/errorLogger/persist.ts - utils/errorLogger/log.ts split (sub-module persist)
// Build payload + insert ke Supabase via RPC log_client_error.

import type { ErrorLogPayload } from './types.js';
import { getErrorType } from './classify.js';
import { isNoiseError } from './rateLimit.js';

// Dynamic import: user.ts menarik supabase/client (±165KB) — jangan ikut entry
// publik. Error jarang terjadi, jadi muat modul user hanya saat benar-benar log.
async function loadUserInfo() {
    const { getCurrentUserInfo } = await import('./user.js');
    return getCurrentUserInfo();
}


export async function buildErrorPayload(
    error: Error | string,
    context?: string,
    additionalInfo?: Record<string, any>
) {
    const userInfo = await loadUserInfo();
    const errorObj = error instanceof Error ? error : new Error(String(error));

    return {
        userId: userInfo.userId,
        userEmail: userInfo.userEmail,
        userRole: userInfo.userRole,
        errorMessage: errorObj.message,
        errorStack: errorObj.stack || null,
        errorContext: context || null,
        errorType: getErrorType(errorObj),
        pageUrl: typeof window !== 'undefined' ? window.location.href : null,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        additionalInfo: additionalInfo || null,
        createdAt: new Date().toISOString()
    };
}

export async function buildPayloadFromPayload(payload: ErrorLogPayload) {
    const userInfo = await loadUserInfo();

    return {
        userId: userInfo.userId,
        userEmail: userInfo.userEmail,
        userRole: userInfo.userRole,
        errorMessage: payload.errorMessage,
        errorStack: payload.errorStack || null,
        errorContext: payload.errorContext || null,
        errorType: payload.errorType || 'unknown',
        pageUrl: typeof window !== 'undefined' ? window.location.href : null,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        additionalInfo: payload.additionalInfo || null,
        createdAt: new Date().toISOString()
    };
}

/**
 * Persist payload ke Supabase via RPC log_client_error (SECURITY DEFINER)
 * agar anon & authenticated sama-sama bisa log. Gagal insert tidak dilempar.
 */
export async function insertErrorLog(payload: Record<string, any>): Promise<void> {
    // Log to console in development
    if (import.meta.env.DEV) {
        console.error('[ErrorLogger]', payload);
    }

    // Localhost / dev JANGAN mengotori error_logs produksi (Supabase prod dipakai
    // bersama oleh vite dev + admin live). Insiden 2026-10-13: refactor FormsPage
    // lokal yang belum selesai membanjiri log-error dengan "getBrandName /
    // openActionMenuId / actionMenuRefs is not defined".
    if (typeof window !== 'undefined') {
        const h = window.location.hostname;
        if (h === 'localhost' || h === '127.0.0.1' || h.endsWith('.local')) return;
    }

    const rpcPayload = { ...payload, id: crypto.randomUUID(), resolved: false };
    // Dynamic import: supabase-js (±165KB) jangan ikut entry publik — error jarang terjadi.
    const { supabase } = await import('../../supabase/client');
    const { error: insertError } = await supabase.rpc('log_client_error', { p: rpcPayload });

    if (insertError && import.meta.env.DEV) {
        console.error('[ErrorLogger] Failed to insert error log:', insertError);
    }
}

export { isNoiseError };
